// SEO component.
//
// React 19 hoists <title>/<meta>/<link> rendered anywhere in the tree into
// <head>, which is why there is no react-helmet dependency here. Do not
// downgrade React below 19 without replacing this mechanism.
//
// Each page passes its built-in defaults. If an admin has saved an override
// for the current path in the `seo` collection, those values win. The override
// query never blocks or breaks rendering: on failure the defaults are used.
import { SITE } from "@/data/site";
import { useSeo } from "@/hooks/content";

/**
 * Social platforms do not resolve relative URLs in og:image, so a path like
 * "/images/hero.webp" has to be made absolute against the canonical domain.
 */
function absoluteUrl(value) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE.url}${value.startsWith("/") ? "" : "/"}${value}`;
}

const DEFAULT_OG = absoluteUrl(SITE.logo);

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  schema,
}) {
  const { data: override } = useSeo(path);

  const resolvedTitleRaw = override?.metaTitle || title;
  const resolvedDescription = override?.metaDescription || description;
  const resolvedImage = absoluteUrl(override?.ogImage || image) || DEFAULT_OG;

  // An admin-supplied meta title is used verbatim — they may want full control
  // of the string, including its length and any suffix.
  const fullTitle = override?.metaTitle
    ? override.metaTitle
    : resolvedTitleRaw
      ? `${resolvedTitleRaw} | ${SITE.shortName} Training & Consultancy`
      : `${SITE.name} — Workplace Safety & Fire Safety Consultancy Singapore`;

  const canonical = `${SITE.url}${path}`;

  const ogTitle = override?.ogTitle || fullTitle;
  const ogDescription = override?.ogDescription || resolvedDescription;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />
      {override?.keywords ? (
        <meta name="keywords" content={override.keywords} />
      ) : null}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
}

// ---- Schema builders (Organisation / ProfessionalService / Service / Article) ----
/**
 * Organisation schema.
 *
 * Takes the live settings so the phone number and social profiles in the
 * structured data follow whatever the admin panel has saved. Callers without
 * access to the settings get the defaults.
 */
export const organizationSchema = (site = SITE) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: SITE.name,
  url: SITE.url,
  telephone: site.mobile,
  email: site.email,
  image: SITE.url + SITE.logo,
  logo: SITE.url + SITE.logo,
  areaServed: "Singapore",
  // Social profiles. Omitted entirely when none are set — an empty sameAs
  // array is worse than no property at all.
  ...(() => {
    const sameAs = [site.facebook, site.instagram, site.tiktok].filter(
      (u) => typeof u === "string" && u.trim().length > 0
    );
    return sameAs.length ? { sameAs } : {};
  })(),
  address: { "@type": "PostalAddress", addressCountry: "SG" },
  description:
    "Singapore-based Workplace Safety & Health and Fire Safety consultancy and training company.",
  identifier: { "@type": "PropertyValue", name: "UEN", value: SITE.uen },
});

export const serviceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.title,
  description: service.excerpt,
  provider: { "@type": "ProfessionalService", name: SITE.name, url: SITE.url },
  areaServed: "Singapore",
  serviceType: service.heroTagline,
});

export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  // Structured data requires an absolute URL, same as og:image.
  image: absoluteUrl(post.image),
  datePublished: post.date,
  author: { "@type": "Organization", name: post.author },
  publisher: {
    "@type": "Organization",
    name: SITE.name,
    logo: { "@type": "ImageObject", url: SITE.url + SITE.logo },
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.url}${it.path}`,
  })),
});
